/**
 * API client for support ticket operations.
 */

interface TicketCreatePayload {
  summary: string;
  description: string;
  error_logs?: string | null;
}

interface TicketComment {
  id: string;
  body: string;
  author_name: string;
  author_email?: string;
  is_public: boolean;
  created_at: string;
}

interface Ticket {
  key: string;
  summary: string;
  description: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface TicketAttachment {
  filename: string;
  size: number;
  mime_type: string;
  created_at: string;
  content_url: string | null;
}

interface TicketDetail extends Ticket {
  comments: TicketComment[];
  attachments: TicketAttachment[];
}

interface TicketListResponse {
  tickets: Ticket[];
  total: number;
}

export type { Ticket, TicketAttachment, TicketComment, TicketDetail, TicketListResponse };

export function getAttachmentDownloadUrl(ticketKey: string, contentUrl: string): string {
  return `/api/support/tickets/${ticketKey}/attachments/download?url=${encodeURIComponent(contentUrl)}`;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `Request failed: ${response.status}`);
  }
  return response.json();
}

export async function createTicket(payload: TicketCreatePayload): Promise<Ticket> {
  const response = await fetch('/api/support/tickets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  return handleResponse<Ticket>(response);
}

export async function listTickets(): Promise<TicketListResponse> {
  const response = await fetch('/api/support/tickets', {
    credentials: 'include',
  });
  return handleResponse<TicketListResponse>(response);
}

export async function getTicketDetail(ticketKey: string): Promise<TicketDetail> {
  const response = await fetch(`/api/support/tickets/${ticketKey}`, {
    credentials: 'include',
  });
  return handleResponse<TicketDetail>(response);
}

export async function addComment(ticketKey: string, body: string): Promise<TicketComment> {
  const response = await fetch(`/api/support/tickets/${ticketKey}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ body }),
  });
  return handleResponse<TicketComment>(response);
}

export async function uploadAttachment(ticketKey: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`/api/support/tickets/${ticketKey}/attachments`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Failed to upload attachment');
  }
}
